import { getApiUrl } from '@/config/env'

import type { ChatRequest, ChatResponse } from './chatbot.api'

const TOKEN_KEY = 'gisify_token'

export type StreamEvent =
  | { event: 'message_start'; data: { sessionId: string; turnId: string; model: string } }
  | { event: 'round_start'; data: { roundNumber: number } }
  | { event: 'text_delta'; data: { delta: string } }
  | { event: 'tool_call_start'; data: { toolCallId: string; name: string; input: unknown; roundNumber: number } }
  | {
      event: 'tool_call_end'
      data: { toolCallId: string; status: 'success' | 'error'; resultSummary: string; durationMs: number }
    }
  | { event: 'round_end'; data: { roundNumber: number; toolCallsCount: number } }
  | {
      event: 'client_tool_request'
      data: { runId: string; toolCalls: Array<{ id: string; name: string; input: unknown }> }
    }
  | { event: 'message_complete'; data: { finalResponse: ChatResponse } }
  | { event: 'error'; data: { code: string; message: string; recoverable: boolean } }
  | { event: 'done'; data: Record<string, never> }

export class StreamTransportError extends Error {
  constructor(public status: number) {
    super(`Stream failed: ${status}`)
  }
}

/**
 * Thrown when the response body's reader reaches `done` WITHOUT ever
 * delivering a terminal event (`message_complete`, `done`, or `error`).
 *
 * Observed live: the chat-stream Lambda died mid-turn. HTTP status was 200
 * (a streaming response commits its status immediately, before the body is
 * known to be well-formed), so `!resp.ok` never caught it — the body was
 * simply a Lambda runtime error with no SSE framing at all. Silently
 * returning from `connectChatStream` in that case left the chatbot machine
 * parked in `sending` forever, with no error and no way back except a
 * reload. Extends `StreamTransportError` so it reaches the machine via the
 * SAME `STREAM_ERROR` path an HTTP-level transport failure already takes
 * (retry-once-as-buffered, rate-limit routing, client-tool-turn handling —
 * see `chatbotMachine.ts`), rather than inventing a second failure mode the
 * machine has to learn to handle.
 */
export class StreamDeadError extends StreamTransportError {
  constructor(public bodyPreview?: string) {
    super(0)
    this.name = 'StreamDeadError'
    this.message = bodyPreview
      ? `Stream ended without completing the turn: ${bodyPreview}`
      : 'Stream ended without completing the turn'
  }
}

const TERMINAL_EVENTS = new Set<StreamEvent['event']>(['message_complete', 'done', 'error'])

/**
 * Best-effort extraction of a human-readable message from a body that never
 * produced a single valid SSE frame — e.g. a Lambda runtime error surfaced as
 * a bare JSON payload instead of `event: error\ndata: {...}`. Returns
 * undefined rather than guessing when the body isn't JSON-shaped text.
 */
function describeNonSseBody(raw: string): string | undefined {
  const trimmed = raw.trim()
  if (!trimmed) return undefined

  try {
    const parsed: unknown = JSON.parse(trimmed)

    if (parsed && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>
      const candidate = obj.errorMessage ?? obj.message ?? obj.error

      if (typeof candidate === 'string') return candidate.slice(0, 300)
    }
  } catch {
    /* not JSON — fall through */
  }

  // Not JSON-shaped: surface a short raw excerpt rather than nothing.
  return trimmed.slice(0, 300)
}

/**
 * Thrown when the backend rejects a chat request with HTTP 429
 * `{code, resetAt}` (PR6 — public AI budget exhausted). Distinct from
 * `StreamTransportError` so the chatbot machine can route it to a friendly
 * banner UX instead of the streaming-retry-as-buffered fallback (which is
 * futile for budget exhaustion). Also emitted when the backend defensively
 * surfaces `code: 'public_budget_exhausted'` post-stream-start as an SSE
 * `error` event.
 */
export class RateLimitError extends Error {
  constructor(
    public code: string,
    public resetAt: string | null,
  ) {
    super(`Rate limited: ${code}`)
    this.name = 'RateLimitError'
  }
}

function resolveStreamUrl(): string {
  // VITE_AI_CHATBOT_STREAM_URL is injected by the deploy pipeline as
  // `https://chat.console.api.{DOMAIN_NAME}/ai/chat/stream` — a deterministic
  // subdomain wired in the CDK (CertificatesStack pinpoint cert + ConsoleAiStack
  // Route 53 A-alias) fronting a dedicated CloudFront distribution backed by a
  // Lambda Function URL (SSE requires response streaming; API Gateway can't do
  // that). Without the env var the SPA falls back to the console API Gateway,
  // which doesn't own this route and returns a preflight 403 → the chatbot
  // auto-retries buffered `/ai/chat` and loses the streaming UX.
  const override = import.meta.env?.VITE_AI_CHATBOT_STREAM_URL as string | undefined
  if (override) return override

  return getApiUrl('/ai/chat/stream')
}

export function parseSSEFrame(frame: string): StreamEvent | null {
  const trimmed = frame.trim()
  if (!trimmed) return null
  const lines = trimmed.split('\n')
  const eventLine = lines.find(l => l.startsWith('event: '))
  const dataLine = lines.find(l => l.startsWith('data: '))
  if (!eventLine || !dataLine) return null
  const event = eventLine.slice('event: '.length) as StreamEvent['event']

  try {
    const data = JSON.parse(dataLine.slice('data: '.length))

    return { event, data } as StreamEvent
  } catch {
    return null
  }
}

async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text)
  const hashBuf = await crypto.subtle.digest('SHA-256', bytes)

  return Array.from(new Uint8Array(hashBuf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

export async function connectChatStream(
  req: ChatRequest,
  signal: AbortSignal,
  send: (event: StreamEvent) => void,
): Promise<void> {
  const token = localStorage.getItem(TOKEN_KEY)
  const body = JSON.stringify(req)
  // Two header quirks required by CloudFront Origin Access Control → Lambda
  // Function URL:
  //   1. JWT goes in X-Auth-Token, not Authorization — OAC won't add its SigV4
  //      signature if the viewer already sent Authorization, then Function URL
  //      rejects with InvalidSignatureException.
  //   2. Lambda Function URLs invoked via OAC do NOT accept unsigned payloads
  //      (per AWS docs). Viewer must compute SHA256(body) and include it in
  //      x-amz-content-sha256 before CloudFront signs; otherwise CloudFront
  //      signs with the UNSIGNED-PAYLOAD placeholder and Lambda's real body
  //      hash won't match → InvalidSignatureException.
  // Must stay in sync with chat-stream.ts + dev-server streaming-dispatch.
  const bodySha256 = await sha256Hex(body)
  const resp = await fetch(resolveStreamUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-amz-content-sha256': bodySha256,
      ...(token ? { 'X-Auth-Token': `Bearer ${token}` } : {}),
    },
    body,
    signal,
  })

  // PR6-2: 429 → typed RateLimitError with parsed {code, resetAt}.
  // Routed by chatbotMachine to a friendly banner instead of the
  // streaming-retry-as-buffered fallback (futile for budget exhaustion).
  if (resp.status === 429) {
    let body: { code?: string; resetAt?: string } = {}

    try {
      body = (await resp.json()) as { code?: string; resetAt?: string }
    } catch {
      /* malformed body — fall through to defaults */
    }

    throw new RateLimitError(body.code ?? 'rate_limited', body.resetAt ?? null)
  }

  if (!resp.ok || !resp.body) throw new StreamTransportError(resp.status)

  const reader = resp.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''
  let sawTerminalEvent = false

  while (true) {
    const { value, done } = await reader.read()

    if (done) {
      if (sawTerminalEvent) return
      // The body ended without ever delivering message_complete/done/error —
      // a dead stream (see StreamDeadError). `buffer` may hold a trailing
      // non-SSE payload (a bare JSON runtime-error body never framed as SSE
      // at all) worth surfacing.
      throw new StreamDeadError(describeNonSseBody(buffer))
    }

    buffer += value
    let frameEnd

    while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, frameEnd)
      buffer = buffer.slice(frameEnd + 2)
      const ev = parseSSEFrame(frame)

      if (ev?.event === 'error' && (ev.data as { code?: string })?.code === 'public_budget_exhausted') {
        // PR6-2: defensively-thrown LimitExceeded post-stream-start surfaces
        // as a structured SSE `error` event. Promote to RateLimitError so the
        // machine routes to the rateLimited state.
        throw new RateLimitError((ev.data as { code: string }).code, null)
      }

      if (ev && TERMINAL_EVENTS.has(ev.event)) sawTerminalEvent = true
      if (ev) send(ev)
      if (ev?.event === 'done') return
    }
  }
}
